import { NavLink } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Container } from "./Container";

/**
 * App navbar. Receives nav items (to, label, icon component optional) and optional logo/brand.
 * Pass `logoMarkSrc` for a square mark image (same rounded box size as the default icon tile).
 * No hardcoded brand name; pass brandLabel for easy rebrand.
 */
export function Navbar({
  logo,
  logoMarkSrc,
  logoIcon: LogoIcon,
  brandLabel,
  navItems = [],
  accountTo = "/account",
  accountLabel = "Account",
  accountIcon: AccountIcon,
  className,
  ...props
}) {
  return (
    <header
      className={cn(
        "z-50 border-b border-border/60",
        /*
         * Mobile (iOS): opaque bar edge-to-edge under notch/status bar. Translucent + backdrop-blur
         * often leaves a visible gap above the header; desktop keeps glass treatment.
         */
        "max-md:fixed max-md:left-0 max-md:right-0 max-md:top-0 max-md:w-full max-md:max-w-none",
        "max-md:isolate max-md:bg-card max-md:pt-safe-area-top max-md:shadow-sm",
        "max-md:[-webkit-transform:translateZ(0)] max-md:[transform:translateZ(0)]",
        "md:relative md:sticky md:top-0 md:bg-card/90 md:backdrop-blur-md md:shadow-none",
        className
      )}
      data-testid="main-header"
      {...props}
    >
      <Container className="relative z-10">
        <div className="flex h-16 sm:h-[4.5rem] items-center justify-between gap-5">
          <NavLink
            to="/"
            className="flex items-center gap-3 shrink-0"
            data-testid="logo-link"
          >
            {logo ?? (
              logoMarkSrc ? (
                <div
                  className="flex h-10 w-10 shrink-0 overflow-hidden rounded-lg sm:h-11 sm:w-11"
                  aria-hidden
                >
                  <img
                    src={logoMarkSrc}
                    alt=""
                    className="h-full w-full object-cover"
                    draggable={false}
                  />
                </div>
              ) : (
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground sm:h-11 sm:w-11"
                  aria-hidden
                >
                  {LogoIcon ? <LogoIcon className="h-5 w-5 sm:h-6 sm:w-6" /> : null}
                </div>
              )
            )}
            {brandLabel != null && (
              <span className="font-heading text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                {brandLabel}
              </span>
            )}
          </NavLink>

          <nav className="hidden md:flex items-center gap-3 lg:gap-4" data-testid="main-nav">
            {navItems.map(({ to, label, icon: Icon, badge, pulse }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2.5 rounded-lg px-5 py-2.5 text-lg font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  )
                }
              >
                {Icon ? (
                  <span className="relative inline-flex">
                    <Icon className={cn("h-5 w-5 shrink-0", pulse && "animate-pulse")} />
                    {pulse ? (
                      <span className="absolute -inset-1 rounded-full bg-primary/20 animate-ping" />
                    ) : null}
                  </span>
                ) : null}
                {label}
                {typeof badge === "number" && badge > 0 ? (
                  <span
                    className={cn(
                      "ml-1 inline-flex min-w-6 h-6 items-center justify-center rounded-full px-1.5 text-xs font-semibold",
                      "bg-primary text-primary-foreground",
                      pulse && "animate-bounce"
                    )}
                    data-testid={`nav-badge-${label.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    {badge > 99 ? "99+" : badge}
                  </span>
                ) : null}
              </NavLink>
            ))}
          </nav>

          <NavLink
            to={accountTo}
            data-testid="nav-account"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-2.5 rounded-full px-5 py-2.5 text-base font-medium transition-colors shrink-0",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground"
              )
            }
          >
            {AccountIcon ? <AccountIcon className="h-5 w-5 shrink-0" /> : null}
            <span className="hidden sm:inline">{accountLabel}</span>
          </NavLink>
        </div>
      </Container>

      {/* Mobile: compact top bar only; bottom tabs in Layout */}
    </header>
  );
}
